import React, { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Resizable } from 're-resizable';
import { Tooltip } from '@mui/material';
import { cloneDeep } from 'lodash';
import cs from 'classnames';
import ArrowDown from '@mui/icons-material/KeyboardArrowDown';
import ArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { useOnClickOutside } from 'usehooks-ts';

import { getAllOrganizations, getOrganizationRequest } from '../../api/organizationsApi';
import DeleteUserModal from '../modules/Users/DeleteUserModal/DeleteUserModal';
import EditUserAdminModal from '../modules/Users/EditUserModal/EditUserModal';
import { ContainedButton, RolesButton } from '../modules/Buttons';
import CreateOrganizationModal from './CreateOrganizationModal';
import { PaginationFooter } from '../modules/PaginationFooter';
import { DateFormatter, useDimensions } from '../../helpers';
import { ResizeIcon } from '../../assets/icons/resizeIcon';
import OrganizationCard from '../modules/OrganizationCard';
import LinearProgress from '../modules/LinearProgress';
import EditIcon from '../../assets/icons/edit-pen.svg';
import CreateOrgUserModal from './CreateOrgUserModal';
import styles from './OrganizationsPage.module.scss';
import useDebounce from '../../helpers/useDebounce';
import { ROLES, RolesLowerCase } from '../../enums';
import { ORGANIZATION } from '../../enums/TYPES';
import SearchInput from '../modules/SearchInput';
import TrashButton from '../modules/TrashButton';
import RolePlate from '../modules/RolePlate';
import Avatar from '../modules/Avatar';
import FullPageLoader from '../modules/FullPageLoader';
import { dateToFormattedHours } from '../../helpers/dateToFormattedHours';
import { MoreIcon } from '../../assets/icons/threedots';
import ServerS3StorageSummary from './S3StorageDetails/ServerS3StorageSummary';

enum ROLE_OPTIONS {
  ALL = 'All',
}

type ROLES_SELECTOR = ROLES | ROLE_OPTIONS;

const organizationPaginationLimit = 'organizationPaginationLimit';
const setOrganizationPaginationLimit = (val: number) => {
  return localStorage.setItem(organizationPaginationLimit, `${val}`);
};

const getOrganizationPaginationLimit = () => {
  const res = localStorage.getItem(organizationPaginationLimit);
  if (res) {
    return +res;
  } else {
    // default is 10
    return 10;
  }
};

const OrganizationsManagement = () => {
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [orgs, setOrgs] = useState<ORGANIZATION[]>([]);
  const [loading, setLoading] = useState(false);
  const { widthD } = useDimensions();

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortField, setSortField] = useState<
    | 'name'
    | 'location'
    | 'total_users'
    | 'storage_usage'
    | 'hours_limit_used'
    | 'download_hours_limit_used'
    | 'period_end'
    | 'projected_hours_at_the_end'
    | 'created_at'
  >('name');

  const [selectedType, setSelectedType] = useState<'all' | 'child' | 'parent'>('all');
  const [organizationStatus, setOrganizationStatus] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(getOrganizationPaginationLimit());
  const [search, setSearch] = useState('');
  const onRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onOrgSelect(null);
    setOrganizationPaginationLimit(parseInt(e.target.value, 10));
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onOrgSelect(null);
    setPage(newPage);
  };

  const debouncedValue = useDebounce<string>(search, 500);
  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onOrgSelect(null);
    setSearch(e.target.value.trim());
    setPage(0);
  };

  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedValue, selectedType, sortField, sortOrder, organizationStatus]);

  const [selectedOrg, setSelectedOrg] = useState<ORGANIZATION | null>(null);

  const fetchOrganizations = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const limit = getOrganizationPaginationLimit();
      getAllOrganizations({ page, limit, search, selectedType, sort: sortOrder, sortField, deletion_status: organizationStatus })
        .then((res: { data: { documents: ORGANIZATION[]; total: number } }) => {
          setOrgs(() => res.data.documents);
          setTotal(res.data.total);
          resolve(res.data);
          const orgInFrame = res.data.documents.find((org) => org.id === selectedOrg?.id);
          if (orgInFrame) {
            onOrgSelect(cloneDeep(orgInFrame));
          } else {
            setSelectedOrg(null);
          }
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const switchModal = () => {
    onOrgSelect(null);
    setShowAddModal(!showAddModal);
  };

  const onOrgSelect = async (org: ORGANIZATION | null, mouseClicked?: boolean) => {
    if (!org || org.deletion_status === 'ARCHIVED') {
      setSelectedOrg(null);
      return;
    }

    if (loading) {
      return;
    }

    if (mouseClicked && selectedOrg?.id === org?.id) {
      setSelectedOrg(null);
      return;
    }

    if (org?.id) {
      try {
        setLoading(true);
        const { data: orgObject } = await getOrganizationRequest(org?.id);
        const users = orgObject.organization_users;
        setSelectedOrg({ ...orgObject, users });
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const [addUserModal, setAddUserModal] = useState(false);
  const switchAddUserModal = () => {
    setAddUserModal(!addUserModal);
  };

  const [searchUserText, setSearchUserText] = useState('');
  const onSearchUser = (e: any) => {
    setSearchUserText(e.target.value.toLocaleLowerCase());
  };

  const [userEditModal, setUserEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<any>(null);
  const switchUserEditModal = () => {
    setUserEditModal(!userEditModal);
  };

  const selectUserToEdit = (u: any) => {
    switchUserEditModal();
    setUserToEdit(u);
  };

  const [userDeleteModal, setUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const switchUserDeleteModal = () => {
    setUserDeleteModal(!userDeleteModal);
  };
  const selectUserToDelete = (u: any) => {
    switchUserDeleteModal();
    setUserToDelete(u);
  };

  const [currentRole, setCurrentRole] = useState<any>(ROLE_OPTIONS.ALL);
  const setRoleFilter = (role: ROLES_SELECTOR) => {
    setCurrentRole(role);
  };

  const deselectOrg = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setSelectedOrg(null);
    }
  };

  // 1. Load sort prefs on mount
  useEffect(() => {
    const order = localStorage.getItem('org_list_sortOrder');
    const field = localStorage.getItem('org_list_sortField');
    if (order) setSortOrder(order as 'asc' | 'desc');
    if (field) setSortField(field as typeof sortField);
  }, []);

  // 2. Persist whenever user sorts
  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      const next = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(next);
      localStorage.setItem('org_list_sortOrder', next);
    } else {
      setSortField(field);
      setSortOrder('desc');
      localStorage.setItem('org_list_sortField', field);
      localStorage.setItem('org_list_sortOrder', 'desc');
    }
  };

  const [orgUsers, setOrgUsers] = useState(selectedOrg?.organization_users);
  useEffect(() => {
    const editedOrgUsers = selectedOrg?.organization_users.map((orgUser: any) => {
      if (orgUser.last_online) {
        const updatedLastOnline = dateToFormattedHours(orgUser.last_online);

        return {
          ...orgUser,
          last_online: updatedLastOnline,
        };
      }

      return orgUser;
    });

    setOrgUsers(editedOrgUsers);
  }, [selectedOrg?.organization_users]);

  const orgListRef = useRef<HTMLDivElement>(null);
  const orgListScrollPositionRef = useRef(0);

  const handleOrgListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    orgListScrollPositionRef.current = e.currentTarget.scrollTop;
  };

  // after orgs change (first load), jiggle scroll
  useEffect(() => {
    const el = orgListRef.current;
    if (!el) return;
    el.scrollTop = 1;
    el.scrollTop = 0;
    orgListScrollPositionRef.current = 0;
  }, [orgs.length]);

  useEffect(() => {
    const el = orgListRef.current;
    if (!el) return;
    el.scrollTop = orgListScrollPositionRef.current;
  }, [selectedOrg]);

  // -------- Shared 3-dots menu for org users (right panel) --------
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [menuUser, setMenuUser] = useState<any | null>(null);

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const openUserMenu = (e: React.MouseEvent<HTMLDivElement>, orgUser: any) => {
    e.preventDefault();
    e.stopPropagation();

    const rowElement = e.currentTarget.closest(`.${styles.container}`) as HTMLElement;
    if (!rowElement) return;

    const rowRect = rowElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 190;
    const menuHeight = actionMenuRef.current?.offsetHeight || 120;

    let left = e.clientX;
    let top = e.clientY;

    // clamp horizontally inside row boundaries
    const maxLeft = rowRect.right - menuWidth;
    if (left > maxLeft) {
      left = Math.max(maxLeft, rowRect.left);
    }
    if (left < rowRect.left) {
      left = rowRect.left;
    }

    // vertical clamp vs viewport bottom
    const viewportBottom = window.innerHeight;
    if (top + menuHeight > viewportBottom) {
      const diff = top + menuHeight - viewportBottom;
      if (diff > 90) {
        top = Math.max(top - (menuHeight - 100), 0);
      } else {
        top = Math.max(top - diff, 0);
      }
    }

    setMenuPosition({ x: left, y: top });
    setMenuUser(orgUser);
    setShowDotsMenu(true);
  };

  const handleMenuEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!menuUser) return;
    selectUserToEdit(menuUser.user);
    setShowDotsMenu(false);
  };

  const handleMenuDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!menuUser) return;
    selectUserToDelete(menuUser.user);
    setShowDotsMenu(false);
  };
  // ---------------------------------------------------------------

  return (
    <div className={styles.container} onClick={deselectOrg}>
      {/* <FullPageLoader loading={loading} /> */}
      {!selectedOrg ? (
        <div className={styles.orgSection}>
          <div className="mb-5">
            <div className={styles.pageTitle}>
              <span className={styles.span}>Organisations Management</span>
              <div className={styles.pageTitleActions}>
                <ServerS3StorageSummary />
                <ContainedButton className={styles.addOrgButton} onClick={switchModal}>
                  + Add Organisation
                </ContainedButton>
              </div>
            </div>
            <div className={styles.toolbar}>
              <SearchInput value={search} className={styles.search} onChange={onSearch} />

              <div>
                {(['ACTIVE', 'ARCHIVED'] as const).map((status) => (
                  <RolesButton
                    key={status}
                    kind={organizationStatus === status ? 'white' : 'gray'}
                    hover={organizationStatus !== status ? 'white' : 'none'}
                    onClick={() => {
                      setSelectedOrg(null);
                      setPage(0);
                      setOrganizationStatus(status);
                    }}
                    customStyles={{ borderRadius: '4px', marginRight: '8px' }}
                  >
                    <div className={styles.roleButton}>
                      {status === 'ACTIVE' ? 'Active' : 'Archived'}
                      {organizationStatus === status && <CheckIcon sx={{ height: '12px' }} />}
                    </div>
                  </RolesButton>
                ))}
              </div>

              <div>
                <RolesButton
                  kind={selectedType === 'all' ? 'white' : 'gray'}
                  hover={selectedType !== 'all' ? 'white' : 'none'}
                  onClick={() => setSelectedType('all')}
                  customStyles={{ borderRadius: '4px' }}
                >
                  <div className={styles.roleButton}>All {selectedType === 'all' && <CheckIcon sx={{ height: '12px' }} />}</div>
                </RolesButton>
                <RolesButton
                  kind={selectedType === 'parent' ? 'blue' : 'gray'}
                  hover={selectedType !== 'parent' ? 'blue' : 'none'}
                  onClick={() => setSelectedType('parent')}
                  customStyles={{ borderRadius: '4px', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>Parent {selectedType === 'parent' && <CheckIcon sx={{ height: '12px' }} />}</div>
                </RolesButton>

                <RolesButton
                  kind={selectedType === 'child' ? 'orange' : 'gray'}
                  hover={selectedType !== 'child' ? 'orange' : 'none'}
                  onClick={() => setSelectedType('child')}
                  customStyles={{ borderRadius: '4px', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>Child {selectedType === 'child' && <CheckIcon sx={{ height: '12px' }} />}</div>
                </RolesButton>
              </div>
            </div>
          </div>
          {orgs.length ? (
            <div className={cs('mb-5', styles.gridContainer)}>
              <div className={styles.listContainer}>
                <div
                  className={cs(
                    styles.listHeader,
                    styles.gridRow,
                    widthD > 1024 && !selectedOrg ? styles.withLocation : styles.compact,
                  )}
                >
                  <div className={styles.name} onClick={() => handleSort('name')}>
                    Name{' '}
                    {sortField === 'name' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: '17px' }} /> : <ArrowDown style={{ fontSize: '17px' }} />)}
                  </div>
                  {widthD > 1024 && !selectedOrg && (
                    <div
                      style={{ fontSize: sortField === 'location' ? '10px' : '13px' }}
                      className={styles.location}
                      onClick={() => handleSort('location')}
                    >
                      Location{' '}
                      {sortField === 'location' &&
                        (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                    </div>
                  )}

                  <div
                    style={{ fontSize: sortField === 'storage_usage' ? '10px' : '13px' }}
                    className={styles.storage}
                    onClick={() => handleSort('storage_usage')}
                  >
                    Storage{' '}
                    {sortField === 'storage_usage' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                  </div>

                  <div
                    style={{ fontSize: sortField === 'hours_limit_used' ? '10px' : '13px' }}
                    className={styles.storage}
                    onClick={() => handleSort('hours_limit_used')}
                  >
                    Upload <br/> Hours
                    {sortField === 'hours_limit_used' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                  </div>

                  <div
                    style={{ fontSize: sortField === 'download_hours_limit_used' ? '10px' : '13px' }}
                    className={styles.storage}
                    onClick={() => handleSort('download_hours_limit_used')}
                  >
                    Download <br /> Hours
                    {sortField === 'download_hours_limit_used' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                  </div>

                  <div
                    style={{ fontSize: sortField === 'period_end' ? '10px' : '13px' }}
                    className={styles.date}
                    onClick={() => handleSort('period_end')}
                  >
                    {widthD > 1400 ? 'End Date' : 'End'}{' '}
                    {sortField === 'period_end' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                  </div>

                  <Tooltip title={'Projected Hours at End Date'}>
                    <div
                      style={{ fontSize: sortField === 'projected_hours_at_the_end' ? '10px' : '13px' }}
                      className={styles.estim}
                      onClick={() => handleSort('projected_hours_at_the_end')}
                    >
                      Projected <br /> Hours
                      {sortField === 'projected_hours_at_the_end' &&
                        (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                    </div>
                  </Tooltip>

                  <div
                    style={{ fontSize: sortField === 'total_users' ? '10px' : '13px' }}
                    className={styles.userLimit}
                    onClick={() => handleSort('total_users')}
                  >
                    Users{' '}
                    {sortField === 'total_users' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                  </div>

                  <div
                    style={{ fontSize: sortField === 'created_at' ? '10px' : '13px' }}
                    className={styles.created}
                    onClick={() => handleSort('created_at')}
                  >
                    {widthD > 1400 ? 'Created Date' : 'Created'}{' '}
                    {sortField === 'created_at' &&
                      (sortOrder === 'asc' ? <ArrowUp style={{ fontSize: 17 }} /> : <ArrowDown style={{ fontSize: 17 }} />)}
                  </div>

                  <div className={styles.actionBtns}>Action</div>
                </div>
                <div className={styles.orgList} ref={orgListRef} onScroll={handleOrgListScroll}>
                  {orgs.map((org) => (
                    <OrganizationCard
                      loading={loading}
                      selectedOrg={selectedOrg}
                      onOrgSelect={onOrgSelect}
                      key={org.id}
                      data={org}
                      fetchAll={fetchOrganizations}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className={cs(styles.orgNotFound, 'mb-5')}>
              <h1 className={styles.title}>No organisations found.</h1>
            </div>
          )}
          <CreateOrganizationModal fetchOrganizations={fetchOrganizations} switchModal={switchModal} open={showAddModal} />
          <PaginationFooter
            total={total}
            page={page}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
          />
        </div>
      ) : (
        <>
          <Resizable
            enable={{
              right: selectedOrg ? true : false,
              top: false,
              bottom: false,
              left: false,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false,
            }}
            maxWidth={selectedOrg ? '60%' : '100%'}
            minWidth={'40%'}
            defaultSize={{
              width: '100%',
              height: '90%',
            }}
            handleComponent={{
              right: (
                <>
                  <div
                    style={{
                      display: selectedOrg ? 'block' : 'none',
                      right: '-20px',
                      width: '3px',
                      height: 'calc(100vh - 150px)',
                      background: '#183047',
                      borderRadius: '12px',
                      position: 'relative',
                    }}
                  >
                    <div style={{ position: 'absolute', borderRadius: '12px', left: '-21px', top: '53px' }}>
                      <ResizeIcon />
                    </div>
                  </div>
                </>
              ),
            }}
          >
            <div className={styles.orgSection}>
              <div className="mb-5" onClick={deselectOrg}>
                <div className={styles.pageTitle} onClick={deselectOrg}>
                  <span className={styles.span} onClick={deselectOrg}>
                    Organisations Management
                  </span>
                  <div className={styles.pageTitleActions}>
                    <ContainedButton className={styles.addOrgButton} onClick={switchModal}>
                      + Add Organisation
                    </ContainedButton>
                  </div>
                </div>
                <div className={styles.toolbar} onClick={deselectOrg}>
                  <SearchInput value={search} className={styles.search} onChange={onSearch} />
                </div>
              </div>
              {orgs.length ? (
                <div className={cs('mb-5', styles.gridContainer)} onClick={deselectOrg}>
                  <div className={styles.listContainer} onClick={deselectOrg}>
                    <div
                      className={cs(
                        styles.orgListHeader,
                        styles.gridRow,
                        widthD > 1024 && !selectedOrg ? styles.withLocation : styles.compact,
                      )}
                      onClick={deselectOrg}
                    >
                      <div className={styles.name} onClick={deselectOrg}>
                        Name
                      </div>
                      {widthD > 1024 && !selectedOrg && (
                        <div className={styles.location} onClick={deselectOrg}>
                          Location
                        </div>
                      )}
                      <div className={styles.storage} onClick={deselectOrg}>
                        Storage
                      </div>
                      <div className={styles.storage} onClick={deselectOrg}>
                        Upload Hours
                      </div>
                      <div className={styles.storage} onClick={deselectOrg}>
                        Download Hours
                      </div>
                      <div className={styles.date} onClick={deselectOrg}>
                        End
                      </div>
                      <Tooltip title={'Projected Hours at End Date'}>
                        <div className={styles.estim} onClick={deselectOrg}>
                          Projected
                        </div>
                      </Tooltip>
                      <div className={styles.userLimit} onClick={deselectOrg}>
                        Users
                      </div>
                      <div className={styles.created} onClick={deselectOrg}>
                        Created
                      </div>
                      <div className={styles.actionBtns} onClick={deselectOrg}>
                        Action
                      </div>
                    </div>
                    <div className={styles.orgList} ref={orgListRef} onScroll={handleOrgListScroll}>
                      {orgs.map((org) => (
                        <OrganizationCard
                          loading={loading}
                          selectedOrg={selectedOrg}
                          onOrgSelect={onOrgSelect}
                          key={org.id}
                          data={org}
                          fetchAll={fetchOrganizations}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={cs(styles.orgNotFound, 'mb-5')}>
                  <h1 className={styles.title}>No organisations found.</h1>
                </div>
              )}
              <CreateOrganizationModal fetchOrganizations={fetchOrganizations} switchModal={switchModal} open={showAddModal} />
              <PaginationFooter
                total={total}
                page={page}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPage={rowsPerPage}
                onPageChange={onPageChange}
              />
            </div>
          </Resizable>
          <div className={styles.usersSection}>
            <div className={cs('mb-5', styles.usersHeader)}>
              <div className={styles.pageTitle}>
                <span className={styles.span}>User List</span>
                <ContainedButton className={styles.addOrgButton} onClick={switchAddUserModal}>
                  + Add Members
                </ContainedButton>
              </div>
              <div className={styles.toolbar}>
                <SearchInput className={styles.search} onChange={onSearchUser} />

                <div className={styles.toolbarRoles}>
                  <RolesButton
                    kind={currentRole === ROLE_OPTIONS.ALL ? 'white' : 'gray'}
                    hover={currentRole !== ROLE_OPTIONS.ALL ? 'white' : 'none'}
                    onClick={() => setRoleFilter(ROLE_OPTIONS.ALL)}
                    customStyles={{ borderRadius: '4px' }}
                  >
                    <div className={styles.roleButton}>
                      All {currentRole === ROLE_OPTIONS.ALL && <CheckIcon sx={{ height: '12px !important' }} />}
                    </div>
                  </RolesButton>

                  <RolesButton
                    kind={currentRole === ROLES.ORGANIZATION_ADMIN ? 'orange' : 'gray'}
                    hover={currentRole !== ROLES.ORGANIZATION_ADMIN ? 'orange' : 'none'}
                    onClick={() => setRoleFilter(ROLES.ORGANIZATION_ADMIN)}
                    customStyles={{ borderRadius: '4px', marginLeft: '8px' }}
                  >
                    <div className={styles.roleButton}>
                      OA {currentRole === ROLES.ORGANIZATION_ADMIN && <CheckIcon sx={{ height: '12px !important' }} />}
                    </div>
                  </RolesButton>
                  {selectedOrg.type === 'child' && (
                    <RolesButton
                      kind={currentRole === ROLES.ANALYST ? 'blue' : 'gray'}
                      hover={currentRole !== ROLES.ANALYST ? 'blue' : 'none'}
                      onClick={() => setRoleFilter(ROLES.ANALYST)}
                      customStyles={{ borderRadius: '4px', marginLeft: '8px' }}
                    >
                      <div className={styles.roleButton}>
                        Analyst {currentRole === ROLES.ANALYST && <CheckIcon sx={{ height: '12px !important' }} />}
                      </div>
                    </RolesButton>
                  )}
                  {selectedOrg.type === 'child' && (
                    <RolesButton
                      kind={currentRole === ROLES.VIEWER ? 'green' : 'gray'}
                      hover={currentRole !== ROLES.VIEWER ? 'green' : 'none'}
                      onClick={() => setRoleFilter(ROLES.VIEWER)}
                      customStyles={{ borderRadius: '4px', marginLeft: '8px' }}
                    >
                      <div className={styles.roleButton}>
                        Viewer {currentRole === ROLES.VIEWER && <CheckIcon sx={{ height: '12px !important' }} />}
                      </div>
                    </RolesButton>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.listContainer}>
              <div className={styles.userListHeader}>
                <div className={styles.name}>Name</div>
                <div className={styles.role}>Role</div>
                <div className={styles.join}>Join Date</div>
                <div className={styles.login}>Last Active</div>
                <div className={styles.actionBtns}>Action</div>
              </div>

              <div className={styles.userList}>
                {loading && <LinearProgress />}
                {orgUsers
                  ?.filter(
                    ({ user }: any) =>
                      user?.email?.toLocaleLowerCase()?.includes(searchUserText) ||
                      user?.username?.toLocaleLowerCase()?.includes(searchUserText),
                  )
                  // @ts-ignore
                  ?.filter((user) => currentRole === ROLE_OPTIONS.ALL || user?.role?.title.includes(RolesLowerCase[currentRole]))
                  .map((user: any) => (
                    <div className={styles.user} key={user?.user?.id}>
                      <div className={styles.container} onContextMenu={(e) => openUserMenu(e, user)}>
                        <div className={styles.user}>
                          <div className={styles.info}>
                            <Avatar avatar={user?.user?.avatar_url} name={user?.user?.email} />
                            <div className={styles.name}>
                              {user?.user?.username} <div className={styles.email}>{user?.user?.email}</div>
                            </div>
                          </div>
                        </div>
                        <div className={styles.userRole}>
                          <Tooltip title={!user.user.invitation_token ? 'Current Role' : 'Not active'}>
                            <div className={!user.user.invitation_token ? styles.active : styles.notActive}>
                              {user.role ? <RolePlate userRole={user.role.title ? user.role.title : `${user.role}`} /> : 'No Role'}
                            </div>
                          </Tooltip>
                        </div>
                        <div className={styles.created}>{DateFormatter(user.created_at)}</div>

                        <div className={styles.login}>{user.last_online}</div>
                        <div className={styles.roleAction}>
                          <div className={styles.dotsMenuBtn} onClick={(e) => openUserMenu(e, user)}>
                            <div className={styles.dotIcon}>
                              <MoreIcon />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Shared three-dots menu for right-side users */}
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              display: showDotsMenu ? 'block' : 'none',
              top: `${menuPosition.y}px`,
              left: `${menuPosition.x}px`,
              zIndex: 9999,
            }}
          >
            <div className={styles.menuOption} onClick={handleMenuEdit}>
              <img src={EditIcon} alt="edit" />
              <span>Edit</span>
            </div>
            <div className={styles.menuOption} onClick={handleMenuDelete}>
              <TrashButton />
              <span>Delete</span>
            </div>
          </div>

          {userToEdit && (
            <EditUserAdminModal user={userToEdit} isOpen={userEditModal} switchModal={switchUserEditModal} fetchData={fetchOrganizations} />
          )}
          {userToDelete && (
            <DeleteUserModal
              fetchData={fetchOrganizations}
              switchModal={switchUserDeleteModal}
              isOpen={userDeleteModal}
              user={userToDelete}
              orgId={selectedOrg.id}
            />
          )}
          <CreateOrgUserModal
            open={addUserModal}
            myOrganization={selectedOrg}
            fetchData={fetchOrganizations}
            switchModal={switchAddUserModal}
            customOrgId={selectedOrg.id}
            type={selectedOrg.type}
          />
        </>
      )}
    </div>
  );
};

export default OrganizationsManagement;
